import React, { FC, ReactElement, useContext, useEffect } from 'react'
import { FormProvider, useForm, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { SelfProvisioning as SelfProvisioningLanguage } from '../../../../i18n'
import { insightsURL } from '../../../NonReact/constants/ApiConstants'
import { DEVICE_FILTER_EXTENSIONS_ONLY } from '../../../NonReact/constants/DeviceTypes'
import { RESTMethods } from '../../../NonReact/constants/REST'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { URLConstructor } from '../../../NonReact/utils/URLBuilder'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import Icon, { IconName } from '../../Common/Components/Icon'
import { Table } from '../../Common/Components/Table/Table'
import TextInput from '../../Common/Components/TextInput'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ON_ALL, ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { LanguageContext } from '../../Common/Contexts'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { displayError } from '../../Common/Utils/Messages'
import { FooterActions } from '../Actions/FooterActions'
import { UserTypes } from '../Constants/Constants'
import { CUSTOMER_REFERENCE_FIELD_NAME } from '../Constants/FieldNames'
import { SelfProvisioningConfig, SelfProvisioningConfigContext } from '../Contexts/SelfProvisioningConfigContext'
import { SelfProvisioningContext, SelfProvisioningContextType } from '../Contexts/SelfProvisioningContext'
import { getDeviceCount } from '../../../NonReact/common/API/EntityAPIClient'
import {
    BaseWebhookObject,
    CustomerReferenceForm,
    SelfProvisioningUser
} from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'

/**
 * The review screen for the user to finish up the self-provisioning process.
 */
export const ReviewView: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const {
        api: { insightsTrackerURL, timestampLocale }
    }: SelfProvisioningConfig = useContext(SelfProvisioningConfigContext)

    const { error }: Console = console

    const {
        deleteableTelSysState: [, setDeleteableTelSys],
        deleteablePartitionState: [, setDeleteablePartition],
        cmeState: [cmeName],
        telSysState: [telSysID],
        partitionState: [partitionID],
        deviceCountState: [deviceCount, setDeviceCount],
        usersState: [users],
        formState: [formState],
        nextStep
    }: SelfProvisioningContextType = useContext(SelfProvisioningContext)

    const methods: UseFormMethods<CustomerReferenceForm> = useForm({
        defaultValues: {
            customerReference: ''
        },
        mode: ON_ALL,
        reValidateMode: ON_CHANGE
    })

    const {
        handleSubmit,
        formState: { isValid }
    }: UseFormMethods<CustomerReferenceForm> = methods

    // What to do when the user submits the form.
    const onSubmit = (state: CustomerReferenceForm): void => {
        if (isValid) {
            // From this point onwards if the modal is closed we no longer need to delete the tel server and partition.
            setDeleteableTelSys(null)
            setDeleteablePartition(null)

            const telSysIDCalc: number = telSysID ?? ALL_VALUE
            const partitionIDCalc: number = partitionID ?? ALL_VALUE

            // Construct the base webhook object.
            const baseObject: BaseWebhookObject = {
                userInformation: {
                    cmeName: cmeName ?? ''
                },
                companyInformation: {
                    companyName: formState?.siteName ?? '',
                    companySector:
                        language.get(`Industries_${formState?.siteIndustry}`) ?? formState?.siteIndustry ?? ''
                },
                companyConfig: {
                    telSysID: telSysIDCalc,
                    partitionID: partitionIDCalc,
                    deviceCount: deviceCount ?? 0,
                    users: {
                        reportingSupervisors:
                            users?.filter(
                                (user: SelfProvisioningUser): boolean =>
                                    user.userType === UserTypes.REPORTING_SUPERVISOR
                            )?.length ?? 0,
                        oneUsers:
                            users?.filter((user: SelfProvisioningUser): boolean => user.userType === UserTypes.ONE_USER)
                                ?.length ?? 0
                    }
                },
                otherInformation: {
                    customerReference: state.customerReference,
                    timestamp: new Date().toLocaleDateString(timestampLocale)
                }
            }

            // Fire and forget the request to FPS.
            // We don't care about the response.
            requestJSON(
                URLConstructor.newInstance({
                    baseURL: insightsTrackerURL
                }),
                {
                    method: RESTMethods.POST,
                    body: baseObject
                }
            ).catch(error)

            // Generate the reports into the insights report user.
            requestJSON(insightsURL(telSysID ?? ALL_VALUE, partitionID ?? ALL_VALUE), {
                method: RESTMethods.POST
            }).catch((e: Error): void => {
                // Shove an error into the console for debugging purposes.
                error(e)
            })

            nextStep()
        }
    }

    useEffect((): void => {
        if (telSysID && partitionID) {
            // Get the device count fresh from the API.
            getDeviceCount(telSysID, partitionID, DEVICE_FILTER_EXTENSIONS_ONLY)
                .then(setDeviceCount)
                .catch(displayError)
        }
    }, [])

    return (
        <FormProvider {...methods}>
            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className='review-view'>
                    <div className='rv-title'>
                        {language.get(SelfProvisioningLanguage.SelfProvisioning_ReviewView_Title)}
                    </div>

                    <div className='review-stand'>
                        <Table
                            tableData={{
                                rows: [
                                    {
                                        columns: [
                                            {
                                                content: (
                                                    <span className='rv-table-row-header'>
                                                        <Icon iconName={IconName.TELSERVER} />
                                                        <span>
                                                            {language.get(
                                                                SelfProvisioningLanguage.SelfProvisioning_ReviewView_Table_TelSys
                                                            )}
                                                        </span>
                                                    </span>
                                                )
                                            },
                                            { content: <div className='rv-table-row-content'>{telSysID}</div> }
                                        ]
                                    },
                                    {
                                        columns: [
                                            {
                                                content: (
                                                    <span className='rv-table-row-header'>
                                                        <Icon iconName={IconName.PARTITION} />
                                                        <span>
                                                            {language.get(
                                                                SelfProvisioningLanguage.SelfProvisioning_ReviewView_Table_Partition
                                                            )}
                                                        </span>
                                                    </span>
                                                )
                                            },
                                            {
                                                content: <div className='rv-table-row-content'>{partitionID}</div>
                                            }
                                        ]
                                    },
                                    {
                                        columns: [
                                            {
                                                content: (
                                                    <span className='rv-table-row-header'>
                                                        <Icon iconName={IconName.PHONE} />
                                                        <span>
                                                            {language.get(
                                                                SelfProvisioningLanguage.SelfProvisioning_ReviewView_Table_DeviceCount
                                                            )}
                                                        </span>
                                                    </span>
                                                )
                                            },
                                            {
                                                content: <div className='rv-table-row-content'>{deviceCount}</div>
                                            }
                                        ]
                                    },
                                    {
                                        columns: [
                                            {
                                                content: (
                                                    <span className='rv-table-row-header'>
                                                        <Icon iconName={IconName.PERSON} />
                                                        <span>
                                                            {language.get(
                                                                SelfProvisioningLanguage.SelfProvisioning_ReviewView_Table_UserCount
                                                            )}
                                                        </span>
                                                    </span>
                                                )
                                            },
                                            {
                                                content: (
                                                    <div className='rv-table-row-content'>{users?.length ?? 0}</div>
                                                )
                                            }
                                        ]
                                    }
                                ]
                            }}
                        />
                        <div className='rv-customer-reference-container'>
                            <ValidationErrors name={CUSTOMER_REFERENCE_FIELD_NAME}>
                                <TextInput
                                    labelLeft={language.get(
                                        SelfProvisioningLanguage.SelfProvisioning_ReviewView_CustomerReference_Label
                                    )}
                                    inputName={CUSTOMER_REFERENCE_FIELD_NAME}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: language.get(
                                                SelfProvisioningLanguage.SelfProvisioning_ReviewView_CustomerReference_Validation
                                            )
                                        }
                                    }}
                                />
                            </ValidationErrors>
                        </div>
                    </div>

                    <div className='review-view-confirmation'>
                        {LanguageUtils.formatLanguageEntry(
                            language.get(SelfProvisioningLanguage.SelfProvisioning_ReviewView_ConfirmationText)
                        )}
                    </div>

                    <FooterActions
                        showRightSideButton
                        rightSideButtonType='submit'
                        rightSideButtonLabel={language.get(
                            SelfProvisioningLanguage.SelfProvisioning_FooterActions_Complete
                        )}
                    />
                </div>
            </form>
        </FormProvider>
    )
}
